<template>
  <div class="billing-gloss batch-glosses">
    <div class="loading loading-lg mt-2" v-if="loading"></div>
    <template v-else-if="view">
      <gloss-kpis v-if="hasGlosses" :totals="kpiTotals" :hints="kpiHints"
                  highlight="released" show-bar/>

      <div class="cr-panel cr-panel-pending cr-panel-first" v-if="hasGlosses">
        <div class="cr-panel-head" :class="{ 'cr-panel-head-amber': pendingCount > 0 }">
          <div class="cr-panel-title">
            <fa-icon :icon="['fal', 'file-invoice']" class="cr-panel-icon"/>
            Itens glosados
            <span class="bg-count">{{ visibleItemsCount }}</span>
          </div>
          <div class="cr-panel-right">
            <deadline-clock v-if="showDeadline" :deadline="batch.deadline" :show-hint="false"/>
            <router-link class="bg-deadline-off tooltip tooltip-left" v-else-if="deadlineUnset"
                         :to="planSettingsRoute"
                         data-tooltip="O prazo do recurso vem do contrato do convênio">
              <fa-icon :icon="['fal', 'clock']"/>Prazo não configurado
            </router-link>
            <button class="btn btn-sm btn-icon btn-icon-left" v-if="!importHidden"
                    :class="[headerClass('import'), tipClass(importDisabledReason)]"
                    :data-tooltip="importDisabledReason"
                    :disabled="!!importDisabledReason" @click="openImport">
              <fa-icon :icon="['fal', 'upload']"/>Importar demonstrativo
            </button>
            <button class="btn btn-sm btn-icon btn-icon-left" v-if="!manualHidden"
                    :class="[headerClass('manual'), tipClass(manualDisabledReason)]"
                    :data-tooltip="manualDisabledReason"
                    :disabled="!!manualDisabledReason" @click="openManualGloss()">
              <fa-icon :icon="['fal', 'plus']"/>Glosa manual
            </button>
          </div>
          <div class="cr-panel-subtitle">{{ panelSubtitle }}</div>
        </div>
        <div class="cr-toolbar">
          <div class="cr-toolbar-field">
            <label class="form-label">Itens</label>
            <div class="btn-group bg-seg">
              <button class="btn btn-sm" :class="filter.onlyGlossed ? 'btn-secondary' : 'btn-gray'"
                      @click="filter.onlyGlossed = true">Só glosados</button>
              <button class="btn btn-sm" :class="!filter.onlyGlossed ? 'btn-secondary' : 'btn-gray'"
                      @click="filter.onlyGlossed = false">Todos</button>
            </div>
          </div>
          <div class="cr-toolbar-field bg-field-status">
            <label class="form-label">Status da glosa</label>
            <st-multi-select v-model="filter.statuses" :items="statusItems"
                             value-key="value" label-key="label"/>
          </div>
          <div class="cr-toolbar-field bg-field-code">
            <label class="form-label">Código</label>
            <select class="form-select" v-model="filter.code">
              <option value="">Todos os códigos</option>
              <option v-for="code in view.codes" :value="code.code" :key="code.code">
                {{ code.code }} {{ code.description }} ({{ code.count }})
              </option>
            </select>
          </div>
          <div class="cr-toolbar-field bg-field-professional">
            <label class="form-label">Profissional</label>
            <select class="form-select" v-model="filter.professionalId">
              <option value="">Todos</option>
              <option v-for="item in view.professionals" :value="String(item._id)"
                      :key="String(item._id)">
                {{ item.name }}
              </option>
            </select>
          </div>
          <div class="cr-toolbar-field cr-toolbar-grow">
            <label class="form-label">Pesquisar</label>
            <input type="text" class="form-input" v-model="filter.search"
                   placeholder="Paciente, nº guia, procedimento">
          </div>
        </div>
        <div class="empty cr-panel-empty" v-if="visibleGuides.length === 0">
          <div class="empty-icon"><fa-icon :icon="['fal', 'filter']" size="2x"/></div>
          <p class="empty-subtitle">{{ filteredEmptyText }}</p>
          <div class="bg-empty-links">
            <button class="btn btn-link" v-if="canShowAllStatuses" @click="showAllStatuses">
              Incluir todos os status
            </button>
            <button class="btn btn-link" v-if="hasFilter" @click="clearFilters">
              Limpar filtros
            </button>
          </div>
        </div>
        <div class="cr-table-scroll" v-else>
          <table class="table table-hover">
            <thead>
            <tr>
              <th class="bg-check">
                <label class="form-checkbox">
                  <input type="checkbox" :checked="allSelected"
                         :indeterminate.prop="partiallySelected" @change="toggleAll">
                  <i class="form-icon"></i>
                </label>
              </th>
              <th>Seq</th>
              <th>Procedimento</th>
              <th>Glosa</th>
              <th class="text-right">Faturado</th>
              <th class="text-right">Glosado</th>
              <th class="text-right hide-sm">Recursado</th>
              <th class="text-right hide-sm">Recuperado</th>
              <th>Status</th>
              <th></th>
            </tr>
            </thead>
            <tbody v-for="guide in visibleGuides" :key="String(guide.accountId)">
            <tr class="bg-guide-tr">
              <td class="bg-check">
                <label class="form-checkbox" v-if="guideSelectable(guide).length > 0">
                  <input type="checkbox" :checked="guideAllSelected(guide)"
                         :indeterminate.prop="guidePartiallySelected(guide)"
                         @change="toggleGuide(guide)">
                  <i class="form-icon"></i>
                </label>
              </td>
              <td colspan="3">
                <span class="bg-guide-name">{{ guideLabel(guide) }}</span>
                <span class="bg-guide-meta" v-if="guide.referral && guide.referral.insurance">
                  · operadora {{ guide.referral.insurance }}
                </span>
                <span class="bg-guide-meta bg-guide-meta-sep">{{ guideMeta(guide) }}</span>
                <template v-if="guide.guideLevel && guideGloss(guide)">
                  <span class="bg-pill bg-pill-red ml-1">Guia inteira</span>
                  <gloss-code-pill class="ml-1" :code="guideGloss(guide).code"
                                   :description="guideGloss(guide).description"/>
                </template>
              </td>
              <td class="text-right tnum">{{ guide.totals.informed | currency }}</td>
              <td class="text-right tnum text-red">−{{ guide.totals.glossed | currency }}</td>
              <td class="text-right tnum hide-sm"
                  :class="guide.totals.appealed ? 'text-amber' : 'text-gray'">
                {{ guide.totals.appealed ? $options.filters.currency(guide.totals.appealed) : '' }}
              </td>
              <td class="text-right tnum hide-sm"
                  :class="guide.totals.recovered ? 'text-green' : 'text-gray'">
                {{ guide.totals.recovered
                  ? `+${$options.filters.currency(guide.totals.recovered)}`
                  : '' }}
              </td>
              <td>
                <gloss-status-badge v-if="guide.guideLevel && guideGloss(guide)"
                                    :status="guideGloss(guide).status"/>
              </td>
              <td class="text-right text-nowrap">
                <template v-if="guide.guideLevel && guideGloss(guide)">
                  <button class="btn btn-sm btn-action btn-icon btn-gray tooltip tooltip-left"
                          :data-tooltip="glossActionTooltip(guideGlosses(guide), 'appeal')"
                          :disabled="!canAppealGlosses(guideGlosses(guide))"
                          @click="appealGlosses(guideGlosses(guide))">
                    <fa-icon :icon="['fal', 'gavel']"/>
                  </button>
                  <button class="btn btn-sm btn-action btn-icon btn-gray tooltip tooltip-left"
                          :data-tooltip="glossActionTooltip(guideGlosses(guide), 'accept')"
                          :disabled="!canAcceptGlosses(guideGlosses(guide))"
                          @click="acceptGlosses(guideGlosses(guide))">
                    <fa-icon :icon="['fal', 'minus-circle']"/>
                  </button>
                  <button class="btn btn-sm btn-action btn-icon btn-gray tooltip tooltip-left"
                          :data-tooltip="manualTooltip(guideRow(guide), 'Editar')"
                          :disabled="!editableGloss(guideRow(guide)) || !canManage"
                          @click="openManualGloss(guide, null, editableGloss(guideRow(guide)))">
                    <fa-icon :icon="['fal', 'edit']"/>
                  </button>
                  <button class="btn btn-sm btn-action btn-icon btn-gray tooltip tooltip-left"
                          :data-tooltip="manualTooltip(guideRow(guide), 'Excluir')"
                          :disabled="!editableGloss(guideRow(guide)) || !canManage"
                          :class="{ loading: removingId === idOf(editableGloss(guideRow(guide))) }"
                          @click="removeGloss(editableGloss(guideRow(guide)))">
                    <fa-icon :icon="['fal', 'trash']"/>
                  </button>
                  <button class="btn btn-sm btn-action btn-icon btn-gray tooltip tooltip-left"
                          data-tooltip="Histórico"
                          :disabled="guideGlosses(guide).length === 0"
                          @click="showHistory(guideRow(guide))">
                    <fa-icon :icon="['fal', 'history']"/>
                  </button>
                </template>
                <button v-else class="btn btn-sm btn-action btn-icon btn-gray tooltip tooltip-left"
                        :data-tooltip="manualDisabledReason || 'Glosa manual nesta guia'"
                        :disabled="!!manualDisabledReason"
                        @click="openManualGloss(guide)">
                  <fa-icon :icon="['fal', 'plus']"/>
                </button>
              </td>
            </tr>
            <tr v-for="item in guide.visibleItems" :key="String(item.itemId)"
                :class="itemRowClass(guide, item)">
              <td class="bg-check">
                <label class="form-checkbox tooltip tooltip-right" v-if="!guide.guideLevel &&
                item.glosses.length > 0"
                       :data-tooltip="itemCheckTooltip(item)">
                  <input type="checkbox" :checked="itemAllSelected(item)"
                         :indeterminate.prop="itemPartiallySelected(item)"
                         :disabled="itemSelectable(item).length === 0"
                         @change="toggleItem(item)">
                  <i class="form-icon"></i>
                </label>
              </td>
              <td><span class="bg-seq">{{ item.sequencialItem }}</span></td>
              <td class="bg-proc">
                <code>
                  {{ item.expense && item.expense.code }}</code><b>
                  {{ item.expense && item.expense.name }}
                </b>
                <small>{{ itemMeta(guide, item) }}</small>
              </td>
              <td>
                <div class="bg-gstack" v-if="item.glosses.length > 0 && !guide.guideLevel">
                  <gloss-code-pill v-for="gloss in item.glosses" :key="idOf(gloss)"
                                   :code="gloss.code" :description="gloss.description"/>
                  <small v-if="appealNote(item)">{{ appealNote(item) }}</small>
                </div>
                <span class="text-xs text-gray"
                      v-else-if="guide.guideLevel && item.glosses.length > 0">
                  decisão na guia
                </span>
                <span class="text-xs text-gray" v-else>sem glosa</span>
              </td>
              <td class="text-right tnum">{{ item.values.informed | currency }}</td>
              <td class="text-right tnum" :class="item.values.glossed ? 'text-red' : 'text-gray'">
                {{ item.values.glossed
                  ? `−${$options.filters.currency(item.values.glossed)}`
                  : '—' }}
              </td>
              <td class="text-right tnum hide-sm"
                  :class="item.values.appealed ? 'text-amber' : 'text-gray'">
                {{ item.values.appealed ? $options.filters.currency(item.values.appealed) : '—' }}
              </td>
              <td class="text-right tnum hide-sm"
                  :class="item.values.recovered ? 'text-green' : 'text-gray'">
                {{ item.values.recovered
                  ? `+${$options.filters.currency(item.values.recovered)}`
                  : '—' }}
              </td>
              <td>
                <gloss-status-badge v-if="!guide.guideLevel && itemStatus(item)"
                                    :status="itemStatus(item)"/>
                <span class="bg-pill bg-pill-green"
                      v-else-if="!guide.guideLevel && item.status === 'ok'">
                  <fa-icon :icon="['fal', 'check']"/>Liberado
                </span>
              </td>
              <td class="text-right text-nowrap">
                <template v-if="!guide.guideLevel">
                  <button class="btn btn-sm btn-action btn-icon btn-gray tooltip tooltip-left"
                          :data-tooltip="glossActionTooltip(item.glosses, 'appeal')"
                          :disabled="!canAppealGlosses(item.glosses)"
                          @click="appealGlosses(item.glosses)">
                    <fa-icon :icon="['fal', 'gavel']"/>
                  </button>
                  <button class="btn btn-sm btn-action btn-icon btn-gray tooltip tooltip-left"
                          :data-tooltip="glossActionTooltip(item.glosses, 'accept')"
                          :disabled="!canAcceptGlosses(item.glosses)"
                          @click="acceptGlosses(item.glosses)">
                    <fa-icon :icon="['fal', 'minus-circle']"/>
                  </button>
                  <button class="btn btn-sm btn-action btn-icon btn-gray tooltip tooltip-left"
                          :data-tooltip="manualTooltip(item, 'Editar')"
                          :disabled="!editableGloss(item) || !canManage"
                          @click="openManualGloss(guide, item, editableGloss(item))">
                    <fa-icon :icon="['fal', 'edit']"/>
                  </button>
                  <button class="btn btn-sm btn-action btn-icon btn-gray tooltip tooltip-left"
                          :data-tooltip="manualTooltip(item, 'Excluir')"
                          :disabled="!editableGloss(item) || !canManage"
                          :class="{ loading: removingId === idOf(editableGloss(item)) }"
                          @click="removeGloss(editableGloss(item), item)">
                    <fa-icon :icon="['fal', 'trash']"/>
                  </button>
                  <button class="btn btn-sm btn-action btn-icon btn-gray tooltip tooltip-left"
                          data-tooltip="Histórico" :disabled="item.glosses.length === 0"
                          @click="showHistory(item)">
                    <fa-icon :icon="['fal', 'history']"/>
                  </button>
                </template>
              </td>
            </tr>
            </tbody>
          </table>
        </div>
        <div class="cr-panel-foot" v-if="visibleGuides.length > 0">
          <span>Faturado <b>{{ visibleTotals.informed | currency }}</b></span>
          <span>Glosado <b class="text-red">−{{ visibleTotals.glossed | currency }}</b></span>
          <span>Recursado <b class="text-amber">{{ visibleTotals.appealed | currency }}</b></span>
          <span>
            Recuperado <b class="text-green">+{{ visibleTotals.recovered | currency }}</b>
          </span>
        </div>
      </div>

      <div class="empty bg-empty" :class="`bg-empty-${emptyState.tone}`" v-else>
        <div class="empty-icon"><fa-icon :icon="['fal', emptyState.icon]" size="2x"/></div>
        <p class="empty-title h5">{{ emptyState.title }}</p>
        <p class="empty-subtitle">{{ emptyState.text }}</p>
        <div class="bg-empty-note">
          <span>Faturado <b class="tnum">{{ kpiTotals.informed | currency }}</b></span>
          <span v-if="showReleased">
            Liberado <b class="tnum text-green">{{ kpiTotals.released | currency }}</b>
          </span>
          <span v-if="kpiHints.informed">{{ kpiHints.informed }}</span>
        </div>
        <div class="empty-action bg-empty-actions" v-if="!importHidden || !manualHidden">
          <button class="btn btn-icon btn-icon-left" v-if="!importHidden"
                  :class="[actionClass('import'), tipClass(importDisabledReason)]"
                  :data-tooltip="importDisabledReason"
                  :disabled="!!importDisabledReason" @click="openImport">
            <fa-icon :icon="['fal', 'upload']"/>Importar demonstrativo
          </button>
          <button class="btn btn-icon btn-icon-left" v-if="!manualHidden"
                  :class="[actionClass('manual'), tipClass(manualDisabledReason)]"
                  :data-tooltip="manualDisabledReason"
                  :disabled="!!manualDisabledReason" @click="openManualGloss()">
            <fa-icon :icon="['fal', 'plus']"/>Glosa manual
          </button>
        </div>
      </div>

      <gloss-selection-bar
        :count="selected.length"
        :total="selectedTotal"
        :accept-disabled-reason="acceptDisabledReason"
        :appeal-disabled-reason="appealDisabledReason"
        :same-round="selected.length > 1"
        :overdue="deadlineOverdue"
        :saving="saving"
        @clear="selected = []"
        @accept="acceptSelected"
        @appeal="appealSelected"
      />
    </template>

    <import-statement-modal
      v-if="modals.import.show"
      :show="modals.import.show"
      :batch-id="batchId"
      :insurance-id="batch ? String(batch.insurance._id) : null"
      @close="closeImport"
      @respond="respondFromStatement"
    />
    <manual-gloss-modal
      v-if="modals.gloss.show"
      :show="modals.gloss.show"
      :batch-id="batchId"
      :view="view"
      :financial="financial"
      :account-id="modals.gloss.accountId"
      :item-id="modals.gloss.itemId"
      :gloss="modals.gloss.gloss"
      @close="closeManualGloss"
    />
    <appeal-modal
      v-if="modals.appeal.show"
      :show="modals.appeal.show"
      :batch-id="batchId"
      :appeal-id="modals.appeal.appealId"
      :gloss-ids="modals.appeal.glossIds"
      @close="closeAppeal"
      @respond="openResponse"
    />
    <appeal-response-modal
      v-if="modals.response.show"
      :show="modals.response.show"
      :appeal-id="modals.response.appealId"
      :prefill="modals.response.prefill"
      @close="closeResponse"
    />
  </div>
</template>

<script>
import moment from 'moment';
import { MANAGE_BILLING_GLOSSES, IMPORT_BILLING_STATEMENTS } from '@/data/actions/modules/financial';
import { statuses as glossStatuses } from '@/data/billing-gloss-statuses';
import { getRoundName } from '@/data/billing-appeal-statuses';
import GlossKpis from './GlossKpis.vue';
import DeadlineClock from './DeadlineClock.vue';
import GlossStatusBadge from './GlossStatusBadge.vue';
import GlossCodePill from './GlossCodePill.vue';
import GlossSelectionBar from './GlossSelectionBar.vue';
import ImportStatementModal from '../modals/ImportStatementModal.vue';
import ManualGlossModal from '../modals/ManualGlossModal.vue';
import AppealModal from '../modals/AppealModal.vue';
import AppealResponseModal from '../modals/AppealResponseModal.vue';
import {
  deadlineInfo, guideLabel, idOf, num, round2,
} from '../helpers';
import { planRoute } from '../../batch/service';

const GLOSSABLE = ['sent', 'analyzed', 'in_appeal'];
const APPEALABLE_BATCH = ['analyzed', 'in_appeal'];
const FINISHED_BATCH = ['settled', 'canceled'];
const RECOVERED = ['overturned', 'partially_overturned'];

const EVENT_NAMES = {
  created: 'Glosa registrada',
  updated: 'Glosa alterada',
  accepted: 'Glosa aceita (perda assumida)',
  appealed: 'Recurso enviado',
  answered: 'Resposta da operadora registrada',
  statement_applied: 'Importada do demonstrativo',
};

function blankFilter() {
  return {
    onlyGlossed: true,
    statuses: ['open', 'maintained', 'appealed'],
    code: '',
    professionalId: '',
    search: '',
  };
}

export default {
  components: {
    GlossKpis,
    DeadlineClock,
    GlossStatusBadge,
    GlossCodePill,
    GlossSelectionBar,
    ImportStatementModal,
    ManualGlossModal,
    AppealModal,
    AppealResponseModal,
  },
  props: {
    batchId: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      loading: true,
      saving: false,
      removingId: null,
      view: null,
      financial: null,
      filter: blankFilter(),
      selected: [],
      modals: {
        import: { show: false },
        gloss: {
          show: false, accountId: null, itemId: null, gloss: null,
        },
        appeal: { show: false, appealId: null, glossIds: [] },
        response: { show: false, appealId: null, prefill: null },
      },
    };
  },
  computed: {
    canManage() {
      return this.$can(MANAGE_BILLING_GLOSSES);
    },
    canImport() {
      return this.$can(IMPORT_BILLING_STATEMENTS);
    },
    batch() {
      return this.view ? this.view.batch : null;
    },
    deadline() {
      return deadlineInfo(this.batch ? this.batch.deadline : null);
    },
    deadlineOverdue() {
      return this.deadline.tone === 'overdue';
    },
    showDeadline() {
      return this.openGlossCount > 0 && !!this.batch.deadline.configured
        && this.deadline.tone !== 'none';
    },
    deadlineUnset() {
      return this.openGlossCount > 0 && !this.batch.deadline.configured;
    },
    planSettingsRoute() {
      const insurance = this.batch && this.batch.insurance;
      if (!insurance) return { path: '/clinical/insurance-plans' };
      return planRoute(idOf(insurance), idOf(insurance.plan));
    },
    statusItems() {
      return Object.keys(glossStatuses).map(key => ({ value: key, label: glossStatuses[key] }));
    },
    finishedBatch() {
      return !!this.batch && FINISHED_BATCH.includes(this.batch.status);
    },
    importHidden() {
      return !this.canImport || this.finishedBatch;
    },
    manualHidden() {
      return !this.canManage || this.finishedBatch;
    },
    importDisabledReason() {
      if (!this.batch) return null;
      if (this.batch.status === 'closed') return 'Envie o lote antes de importar';
      return null;
    },
    manualDisabledReason() {
      if (!this.batch) return null;
      if (!this.canManage) return 'Sem permissão';
      if (this.batch.status === 'closed') return 'Envie o lote antes de registrar glosa';
      if (!GLOSSABLE.includes(this.batch.status)) return 'Lote não aceita glosa';
      return null;
    },
    primaryAction() {
      if (!this.importHidden && !this.importDisabledReason) return 'import';
      if (!this.manualHidden && !this.manualDisabledReason) return 'manual';
      return null;
    },
    sentAppeal() {
      return ((this.view && this.view.appeals) || []).find(appeal => appeal.status === 'sent') || null;
    },
    draftAppeal() {
      return ((this.view && this.view.appeals) || []).find(appeal => appeal.status === 'draft') || null;
    },
    allGlosses() {
      if (!this.view) return [];
      return this.view.guides.flatMap(guide => guide.items.flatMap(item => item.glosses));
    },
    hasGlosses() {
      return this.allGlosses.length > 0;
    },
    openGlossCount() {
      return this.allGlosses.filter(gloss => gloss.status === 'open'
        || (gloss.status === 'maintained' && this.openValue(gloss) > 0)).length;
    },
    pendingCount() {
      if (!this.batch || !APPEALABLE_BATCH.includes(this.batch.status)) return 0;
      return this.openGlossCount;
    },
    glossMap() {
      return new Map(this.allGlosses.map(gloss => [idOf(gloss), gloss]));
    },
    selectedGlosses() {
      return this.selected.map(id => this.glossMap.get(id)).filter(Boolean);
    },
    selectedTotal() {
      return round2(this.selectedGlosses.reduce((sum, gloss) => sum + this.openValue(gloss), 0));
    },
    appealDisabledReason() {
      if (!this.batch) return null;
      if (!this.canManage) return 'Sem permissão';
      if (!APPEALABLE_BATCH.includes(this.batch.status)) return 'Só lote analisado pode ter recurso';
      if (this.selected.length === 0) return 'Selecione ao menos um item';
      if (this.sentAppeal) return `Aguardando resposta do recurso ${this.sentAppeal.number}`;
      return null;
    },
    acceptDisabledReason() {
      if (!this.batch) return null;
      if (!this.canManage) return 'Sem permissão';
      if (!APPEALABLE_BATCH.includes(this.batch.status)) return 'Só lote analisado aceita decisão';
      return null;
    },
    panelSubtitle() {
      if (this.pendingCount > 0 && this.deadlineOverdue) {
        return `Prazo contratual vencido em ${this.deadline.at}. O recurso continua possível se a `
          + 'operadora aceitar; caso contrário, aceite a glosa.';
      }
      if (this.pendingCount > 0) {
        const label = this.pendingCount === 1 ? 'glosa aguarda' : 'glosas aguardam';
        return `${this.pendingCount} ${label} decisão: aceite a perda ou monte o recurso. `
          + 'Marque itens de várias guias para um único recurso.';
      }
      if (this.openGlossCount > 0) {
        return this.batch.status === 'sent'
          ? 'Glosa registrada antes da análise. Aceitar ou recursar fica disponível quando a '
            + 'operadora analisar o lote.'
          : 'Este lote não aceita mais decisão de glosa. Os itens ficam para consulta.';
      }
      return 'Agrupado por guia. Todas as glosas deste lote já foram decididas.';
    },
    canShowAllStatuses() {
      return this.filter.statuses.length < this.statusItems.length;
    },
    filteredEmptyText() {
      if (this.hasFilter) return 'Nenhum item bateu com os filtros aplicados.';
      return 'Nenhuma glosa em aberto, mantida ou em recurso. Inclua todos os status para ver as '
        + 'já resolvidas.';
    },
    showReleased() {
      return this.emptyState.tone === 'done' && this.kpiTotals.released > 0;
    },
    kpiTotals() {
      const totals = this.view ? this.view.totals : {};
      return {
        informed: num(totals.informed),
        glossed: num(totals.glossed),
        glossPercent: num(totals.glossPercent),
        appealed: num(totals.appealed),
        recovered: num(totals.recovered),
        accepted: num(totals.accepted),
        loss: num(totals.accepted),
        released: num(totals.released),
      };
    },
    kpiHints() {
      const totals = this.view ? this.view.totals : {};
      const guides = this.view ? this.view.guides.length : 0;
      const items = this.view
        ? this.view.guides.reduce((sum, guide) => sum + guide.items.length, 0)
        : 0;
      const recoveredCount = this.allGlosses
        .filter(gloss => RECOVERED.includes(gloss.status)).length;
      const answered = this.allGlosses
        .filter(gloss => RECOVERED.includes(gloss.status) || gloss.status === 'maintained').length;
      const acceptedCount = this.allGlosses.filter(gloss => gloss.status === 'accepted').length;
      const receivable = this.financial && this.financial.receivable;
      return {
        informed: `${guides} ${guides === 1 ? 'guia' : 'guias'} · ${items} ${items === 1 ? 'item' : 'itens'}`,
        glossed: `${this.allGlosses.length} ${this.allGlosses.length === 1 ? 'glosa' : 'glosas'} em ${num(totals.itemsGlossed)} ${num(totals.itemsGlossed) === 1 ? 'item' : 'itens'}`,
        appealed: this.sentAppeal
          ? `${this.sentAppeal.number} · enviado ${moment(this.sentAppeal.dates.sentAt).format('DD/MM')}`
          : null,
        recovered: answered ? `${recoveredCount} de ${answered} ${answered === 1 ? 'item acatado' : 'itens acatados'}` : null,
        loss: acceptedCount ? `${acceptedCount} ${acceptedCount === 1 ? 'item' : 'itens'}` : null,
        highlight: receivable
          ? `título ${receivable.document} · ${receivable.status === 'paid' ? 'baixado' : 'aberto'}`
          : `título do lote ${this.batch ? this.batch.number : ''}`,
      };
    },
    searchRegex() {
      const term = this.filter.search.trim();
      if (!term) return null;
      return new RegExp(term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'i');
    },
    visibleGuides() {
      if (!this.view) return [];
      return this.view.guides
        .map(guide => ({
          ...guide,
          visibleItems: guide.items.filter(item => this.itemVisible(guide, item)),
        }))
        .filter(guide => guide.visibleItems.length > 0);
    },
    visibleItemsCount() {
      return this.visibleGuides.reduce((sum, guide) => sum
        + guide.visibleItems.filter(item => item.glosses.length > 0).length, 0);
    },
    visibleTotals() {
      return this.visibleGuides.reduce((acc, guide) => {
        guide.visibleItems.forEach((item) => {
          acc.informed = round2(acc.informed + num(item.values.informed));
          acc.glossed = round2(acc.glossed + num(item.values.glossed));
          acc.appealed = round2(acc.appealed + num(item.values.appealed));
          acc.recovered = round2(acc.recovered + num(item.values.recovered));
        });
        return acc;
      }, {
        informed: 0, glossed: 0, appealed: 0, recovered: 0,
      });
    },
    selectableVisible() {
      return this.visibleGuides.flatMap(guide => this.guideSelectable(guide));
    },
    allSelected() {
      return this.selectableVisible.length > 0
        && this.selectableVisible.every(id => this.selected.includes(id));
    },
    partiallySelected() {
      return this.selected.length > 0 && !this.allSelected;
    },
    hasFilter() {
      const base = blankFilter();
      return this.filter.onlyGlossed !== base.onlyGlossed
        || this.filter.code !== '' || this.filter.professionalId !== '' || this.filter.search.trim() !== ''
        || this.filter.statuses.length !== base.statuses.length
        || !base.statuses.every(status => this.filter.statuses.includes(status));
    },
    emptyState() {
      const status = this.batch ? this.batch.status : null;
      if (status === 'closed') {
        return {
          tone: 'wait',
          icon: 'paper-plane',
          title: 'Lote ainda não enviado',
          text: 'A glosa só existe depois que a operadora analisa o lote. Envie o lote e volte '
            + 'aqui quando o demonstrativo de análise chegar.',
        };
      }
      if (status === 'sent') {
        return {
          tone: 'wait',
          icon: 'clock',
          title: 'Ainda sem retorno da operadora',
          text: 'Importe o demonstrativo de análise (XML) para conciliar as glosas '
            + 'automaticamente, ou registre a glosa informada por portal, e-mail ou carta.',
        };
      }
      if (status === 'canceled') {
        return {
          tone: 'muted',
          icon: 'ban',
          title: 'Lote cancelado',
          text: 'Lote cancelado não recebe glosa nem recurso.',
        };
      }
      return {
        tone: 'done',
        icon: 'check-circle',
        title: 'Nenhuma glosa neste lote',
        text: this.kpiTotals.released > 0
          ? 'A operadora analisou as guias e liberou o valor faturado. Não há nada para aceitar '
            + 'ou recursar.'
          : 'A operadora não glosou nenhum item deste lote. Não há nada para aceitar ou recursar.',
      };
    },
  },
  watch: {
    batchId() {
      this.load();
    },
  },
  mounted() {
    this.load();
  },
  methods: {
    idOf,
    load() {
      this.loading = true;
      return Promise.all([
        this.$http.get('/billing-glosses', { params: { batchId: this.batchId, onlyGlossed: false } }),
        this.$http.get(`/billing-batches/${this.batchId}/financial`).catch(() => ({ data: null })),
      ])
        .then(([view, financial]) => {
          this.view = view.data;
          this.financial = financial.data;
          this.selected = this.selected.filter(id => this.glossMap.has(id));
        })
        .catch((e) => {
          this.$toast.error(e);
        })
        .finally(() => {
          this.loading = false;
        });
    },
    reload() {
      this.$emit('changed');
      return this.load();
    },
    clearFilters() {
      this.filter = blankFilter();
    },
    showAllStatuses() {
      this.filter.statuses = this.statusItems.map(item => item.value);
    },
    actionClass(action) {
      return this.primaryAction === action ? 'btn-primary' : 'btn-gray';
    },
    headerClass(action) {
      return this.pendingCount === 0 && this.primaryAction === action ? 'btn-secondary' : 'btn-gray';
    },
    tipClass(reason) {
      return reason ? 'tooltip tooltip-bottom' : '';
    },
    openValue(gloss) {
      return round2(num(gloss.value) - num(gloss.recoveredValue));
    },
    glossSelectable(gloss) {
      return gloss.status === 'open'
        || (gloss.status === 'maintained' && this.batch && this.batch.allowSecondAppeal);
    },
    itemSelectable(item) {
      return item.glosses.filter(gloss => this.glossSelectable(gloss)).map(idOf);
    },
    guideSelectable(guide) {
      return guide.items.flatMap(item => this.itemSelectable(item));
    },
    guideGlosses(guide) {
      return guide.items.flatMap(item => item.glosses);
    },
    guideRow(guide) {
      return { itemId: null, glosses: this.guideGlosses(guide).filter(gloss => gloss.level !== 'item') };
    },
    guideGloss(guide) {
      return this.guideGlosses(guide).find(gloss => gloss.level !== 'item') || null;
    },
    itemVisible(guide, item) {
      if (this.filter.onlyGlossed && item.glosses.length === 0) return false;
      if (item.glosses.length > 0 && this.filter.statuses.length > 0
        && !item.glosses.some(gloss => this.filter.statuses.includes(gloss.status))) return false;
      if (this.filter.code
        && !item.glosses.some(gloss => gloss.code === this.filter.code)) return false;
      if (this.filter.professionalId
        && idOf(item.professional) !== this.filter.professionalId) return false;
      if (this.searchRegex) {
        const haystack = [
          guide.patient && guide.patient.name,
          guide.referral && guide.referral.provider,
          guide.referral && guide.referral.insurance,
          item.expense && item.expense.name,
          item.expense && item.expense.code,
        ].filter(Boolean).join(' ');
        if (!this.searchRegex.test(haystack)) return false;
      }
      return true;
    },
    itemAllSelected(item) {
      const ids = this.itemSelectable(item);
      return ids.length > 0 && ids.every(id => this.selected.includes(id));
    },
    itemPartiallySelected(item) {
      const ids = this.itemSelectable(item);
      return ids.some(id => this.selected.includes(id)) && !this.itemAllSelected(item);
    },
    guideAllSelected(guide) {
      const ids = this.guideSelectable(guide);
      return ids.length > 0 && ids.every(id => this.selected.includes(id));
    },
    guidePartiallySelected(guide) {
      const ids = this.guideSelectable(guide);
      return ids.some(id => this.selected.includes(id)) && !this.guideAllSelected(guide);
    },
    toggleIds(ids, on) {
      if (on) {
        this.selected = [...new Set([...this.selected, ...ids])];
      } else {
        this.selected = this.selected.filter(id => !ids.includes(id));
      }
    },
    toggleItem(item) {
      this.toggleIds(this.itemSelectable(item), !this.itemAllSelected(item));
    },
    toggleGuide(guide) {
      this.toggleIds(this.guideSelectable(guide), !this.guideAllSelected(guide));
    },
    toggleAll() {
      this.toggleIds(this.selectableVisible, !this.allSelected);
    },
    itemCheckTooltip(item) {
      if (this.itemSelectable(item).length > 0) {
        const maintained = item.glosses.some(gloss => gloss.status === 'maintained');
        return maintained ? 'Mantida: pode ir para a 2ª instância' : null;
      }
      const statuses = item.glosses.map(gloss => gloss.status);
      if (statuses.includes('appealed')) return 'Já em recurso';
      if (statuses.includes('accepted')) return 'Aceita';
      if (statuses.some(status => RECOVERED.includes(status))) return 'Acatada';
      if (statuses.includes('maintained')) return 'Mantida: convênio não aceita 2ª instância';
      return null;
    },
    itemStatus(item) {
      const statuses = item.glosses.map(gloss => gloss.status);
      if (statuses.length === 0) return null;
      if (statuses.includes('appealed')) return 'appealed';
      if (statuses.includes('open')) return 'open';
      if (statuses.includes('partially_overturned')) return 'partially_overturned';
      if (statuses.includes('overturned')) return 'overturned';
      if (statuses.includes('maintained')) return 'maintained';
      return 'accepted';
    },
    itemRowClass(guide, item) {
      const ids = this.itemSelectable(item);
      const status = this.itemStatus(item);
      return {
        active: ids.length > 0 && ids.every(id => this.selected.includes(id)),
        'bg-row-ok': RECOVERED.includes(status),
        'bg-row-dim': status === 'accepted' || (guide.guideLevel && item.glosses.length > 0)
          || item.glosses.length === 0,
      };
    },
    itemMeta(guide, item) {
      if (guide.guideLevel && item.glosses.length > 0 && guide.totals.informed > 0) {
        const share = round2((num(item.values.informed) / num(guide.totals.informed)) * 100);
        return `rateio da glosa da guia · ${String(share).replace('.', ',')}%`;
      }
      const parts = [`qtd ${item.quantity}`];
      if (item.executionDate) parts.push(moment(item.executionDate).format('DD/MM/YYYY'));
      if (item.professional && item.professional.name) parts.push(item.professional.name);
      if (item.glosses.some(gloss => gloss.origin === 'manual')) parts.push('glosa manual');
      return parts.join(' · ');
    },
    appealNote(item) {
      const gloss = item.glosses.find(entry => entry.appeal && entry.appeal.appealId);
      if (!gloss) return null;
      const appeal = (this.view.appeals || [])
        .find(entry => String(entry.id) === String(gloss.appeal.appealId));
      const number = appeal ? appeal.number : 'Recurso';
      const parts = [`${number} · ${getRoundName(gloss.appeal.round)}`];
      if (gloss.appeal.response && gloss.appeal.response.respondedAt) {
        parts.push(`respondido ${moment(gloss.appeal.response.respondedAt).format('DD/MM')}`);
      }
      return parts.join(' · ');
    },
    guideLabel(guide) {
      return guideLabel(guide);
    },
    guideMeta(guide) {
      const parts = [];
      if (guide.patient && guide.patient.name) parts.push(guide.patient.name);
      if (guide.insured && guide.insured.record) parts.push(`carteira ${guide.insured.record}`);
      if (guide.professional && guide.professional.name) parts.push(guide.professional.name);
      if (guide.startDate) parts.push(moment(guide.startDate).format('DD/MM/YYYY'));
      return parts.join(' · ');
    },
    manualTooltip(item, action) {
      if (!this.canManage) return 'Sem permissão';
      return this.editableGloss(item) ? `${action} glosa manual` : 'Só glosa manual aberta';
    },
    editableGloss(item) {
      return item.glosses.find(gloss => gloss.origin === 'manual' && gloss.status === 'open') || null;
    },
    canAppealGlosses(glosses) {
      return this.canManage && !this.sentAppeal && this.batch
        && APPEALABLE_BATCH.includes(this.batch.status)
        && glosses.some(gloss => this.glossSelectable(gloss));
    },
    canAcceptGlosses(glosses) {
      return this.canManage && this.batch && APPEALABLE_BATCH.includes(this.batch.status)
        && glosses.some(gloss => gloss.status === 'open'
          || (gloss.status === 'maintained' && this.openValue(gloss) > 0));
    },
    glossActionTooltip(glosses, action) {
      if (!this.canManage) return 'Sem permissão';
      if (glosses.length === 0) return 'Sem glosa';
      if (action === 'appeal') {
        if (this.sentAppeal) return `Aguardando resposta do ${this.sentAppeal.number}`;
        if (glosses.some(gloss => gloss.status === 'maintained')) {
          return this.batch.allowSecondAppeal ? 'Recursar (2ª instância)' : 'Convênio não aceita 2ª instância';
        }
        if (glosses.some(gloss => gloss.status === 'appealed')) return 'Já em recurso';
        if (glosses.every(gloss => gloss.status === 'accepted')) return 'Aceita';
        if (glosses.some(gloss => RECOVERED.includes(gloss.status))) return 'Acatada';
        return 'Recursar';
      }
      if (glosses.some(gloss => gloss.status === 'appealed')) return 'Já em recurso';
      if (glosses.every(gloss => gloss.status === 'accepted')) return 'Aceita';
      if (glosses.some(gloss => gloss.status === 'maintained')) return 'Aceitar o restante';
      return 'Aceitar (perda)';
    },
    appealGlosses(glosses) {
      const ids = glosses.filter(gloss => this.glossSelectable(gloss)).map(idOf);
      this.openAppeal(ids);
    },
    appealSelected() {
      this.openAppeal([...this.selected]);
    },
    openAppeal(glossIds) {
      if (glossIds.length === 0) return;
      if (this.sentAppeal) {
        this.$dialog.show('', {
          html: '<div class="text-center">'
            + '<h5 class="text-center">Atenção!</h5>'
            + `<div>Já existe um recurso enviado (${this.sentAppeal.number}). Os itens selecionados `
            + 'entram em uma nova rodada quando o recurso for respondido.</div></div>',
          buttons: [{ label: 'Entendi', classes: 'btn-primary' }],
        });
        return;
      }
      this.modals.appeal = {
        show: true,
        appealId: this.draftAppeal ? String(this.draftAppeal.id) : null,
        glossIds,
      };
    },
    closeAppeal(changed) {
      this.modals.appeal = { show: false, appealId: null, glossIds: [] };
      if (changed) {
        this.selected = [];
        this.reload();
      }
    },
    openResponse(appealId, prefill = null) {
      this.modals.appeal = { show: false, appealId: null, glossIds: [] };
      this.modals.response = { show: true, appealId, prefill };
    },
    respondFromStatement({ appealId, prefill }) {
      this.modals.import = { show: false };
      this.openResponse(appealId, prefill);
    },
    closeResponse(saved) {
      this.modals.response = { show: false, appealId: null, prefill: null };
      if (saved) this.reload();
    },
    acceptGlosses(glosses) {
      const ids = glosses
        .filter(gloss => gloss.status === 'open' || gloss.status === 'maintained')
        .map(idOf);
      this.confirmAccept(ids);
    },
    acceptSelected() {
      this.confirmAccept([...this.selected]);
    },
    confirmAccept(ids) {
      if (ids.length === 0) return;
      const glosses = ids.map(id => this.glossMap.get(id)).filter(Boolean);
      const total = round2(glosses.reduce((sum, gloss) => sum + this.openValue(gloss), 0));
      const count = glosses.length;
      this.$dialog.show('', {
        html: '<div class="text-center">'
          + '<h5 class="text-center">Atenção!</h5>'
          + `<div>Aceitar ${count} ${count === 1 ? 'glosa' : 'glosas'} no total de `
          + `${this.$options.filters.currency(total)}? O valor vira perda e não pode mais ser recursado.</div></div>`,
        buttons: [
          { label: 'Não', classes: '' },
          {
            label: 'Sim',
            classes: 'btn-error ml-2',
            click: (close) => {
              close();
              this.accept(ids);
            },
          },
        ],
      });
    },
    accept(ids) {
      this.saving = true;
      return this.$http.post('/billing-glosses/accept', { glossIds: ids })
        .then(({ data }) => {
          this.$toast.success(`${data.accepted} ${data.accepted === 1 ? 'glosa aceita' : 'glosas aceitas'}.`);
          this.selected = [];
          this.reload();
        })
        .catch((e) => {
          this.$toast.error(e);
        })
        .finally(() => {
          this.saving = false;
        });
    },
    openImport() {
      this.modals.import = { show: true };
    },
    closeImport(applied) {
      this.modals.import = { show: false };
      if (applied) this.reload();
    },
    openManualGloss(guide = null, item = null, gloss = null) {
      this.modals.gloss = {
        show: true,
        accountId: guide ? String(guide.accountId) : null,
        itemId: item ? String(item.itemId) : null,
        gloss,
      };
    },
    closeManualGloss(saved) {
      this.modals.gloss = {
        show: false, accountId: null, itemId: null, gloss: null,
      };
      if (saved) this.reload();
    },
    removeGloss(gloss) {
      if (!gloss) return;
      this.$dialog.show('', {
        html: '<div class="text-center">'
          + '<h5 class="text-center">Atenção!</h5>'
          + `<div>Deseja realmente excluir a glosa ${gloss.code} de ${this.$options.filters.currency(gloss.value)}? `
          + 'O título e o repasse voltam ao valor anterior.</div></div>',
        buttons: [
          { label: 'Não', classes: '' },
          {
            label: 'Sim',
            classes: 'btn-error ml-2',
            click: (close) => {
              close();
              this.removingId = idOf(gloss);
              this.$http.delete(`/billing-glosses/${idOf(gloss)}`)
                .then(() => {
                  this.$toast.success('Glosa excluída.');
                  this.reload();
                })
                .catch((e) => {
                  this.$toast.error(e);
                })
                .finally(() => {
                  this.removingId = null;
                });
            },
          },
        ],
      });
    },
    showHistory(item) {
      const rows = item.glosses.flatMap(gloss => (gloss.events || []).map(event => ({
        ...event, code: gloss.code,
      })))
        .sort((a, b) => new Date(a.at) - new Date(b.at));
      const list = rows.length === 0
        ? '<p class="text-gray">Sem eventos registrados.</p>'
        : `<table class="table table-striped"><tbody>${rows.map(event => (
          `<tr><td class="text-nowrap">${moment(event.at).format('DD/MM/YYYY HH:mm')}</td>`
          + `<td class="text-left">${EVENT_NAMES[event.type] || event.type} · glosa ${event.code}`
          + `${event.by && event.by.name ? `<br><small class="text-gray">${event.by.name}</small>` : ''}</td></tr>`
        )).join('')}</tbody></table>`;
      this.$dialog.show('', {
        html: '<div class="text-center"><h5 class="text-center">Histórico</h5>'
          + `<div class="text-left"><strong>${item.expense ? item.expense.name : ''}</strong></div>${list}</div>`,
        buttons: [{ label: 'Fechar', classes: '' }],
      });
    },
  },
};
</script>

<style lang="scss">
@import "../gloss.scss";

.batch-glosses {
  .bg-field-status { width: 9.5rem; }
  .bg-field-code { width: 11rem; }
  .bg-field-professional { width: 9rem; }
  .bg-guide-tr td {
    background-color: $gray-color-ultra-light;
    border-top: $border-color-dark solid $border-width;
  }
  .bg-guide-name { font-weight: 700; }
  .bg-guide-meta { color: $gray-color-dark; font-size: $font-size-xs; }
  .bg-guide-meta-sep { margin-left: $layout-spacing; }
  .form-checkbox.tooltip::after { white-space: nowrap; }
  @media screen and (max-width: $size-md) {
    .cr-toolbar .cr-toolbar-field { flex: 1 1 45%; width: auto; }
  }
}
</style>
